import { UIProvider } from "@marketplace/ui";
import { DATES_THEME } from "@marketplace/ui/dates";
import { Notifications } from "@marketplace/ui/notifications";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  RouterProvider as TanstackRouterProvider,
  useLocation
} from "@tanstack/react-router";
import { ErrorBoundary } from "react-error-boundary";

import { useAppQueryClient } from "@/configs";

import { LampuSellerLogo } from "./assets/logo/lampu-seller-logo";
import { useAuth, useGetProfileDataQuery } from "./modules/auth";
import { router } from "./router";

export const NotificationProvider = () => {
  const { pathname } = useLocation();

  const isReviewPage =
    pathname === "/product/review" ||
    /^\/products(\/list)?\/[^/]+\/review$/.test(pathname);

  if (isReviewPage) {
    return (
      <Notifications containerWidth={679} position="top-center" limit={3} />
    );
  }

  return <Notifications position="top-center" limit={3} />;
};

export const AppProviders = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useAppQueryClient();

  return (
    <ErrorBoundary fallback={<div>Something went wrong</div>}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <UIProvider
          themeConfig={{
            fontFamily: "var(--font-open-sans)"
          }}
          themeOverrides={[DATES_THEME]}
        >
          {children}
        </UIProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export const RouterProvider = () => {
  const { isAuthenticated, userData } = useAuth();
  const { data: profileData, isLoading } = useGetProfileDataQuery({
    options: {
      enabled: isAuthenticated,
      staleTime: 5 * 60 * 1000 // Cache for 5 minutes
    }
  });

  const userMenus = profileData?.data?.menus || [];

  // Show loading state while fetching profile data for authenticated users
  // This prevents route guards from running with empty userMenus
  if (isAuthenticated && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse">
          <LampuSellerLogo />
        </div>
      </div>
    );
  }

  return (
    <TanstackRouterProvider
      router={router}
      context={{
        auth: {
          isAuthenticated,
          userData,
          userMenus
        }
      }}
    />
  );
};
